import _ from 'lodash';
import classnames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import analytics from 'scripts/lib/analytics';
import { PLAYBACK_RATES, useAudioPlayback } from 'components/contexts/audio_playback';

import DropdownMenu, { OpenerButton } from 'components/common/dropdown_menu';
import Timer from 'components/lib/date/timer';

const PlaybackRateOpener = styled(OpenerButton)`
  && {
    background: transparent;
    border: none;
    color: inherit;
    font-size: var(--font-size-reduced);
    font-weight: normal;
    min-height: 0;
    min-width: 0;
    opacity: 0.8;
    padding: 0;
    &:focus,
    &:hover {
      background: transparent;
      border: none;
    }
  }
`;

function WavePlayer({ contentType, customerId, duration: propDuration, onReady, recordingId, url, disabled }) {
  const waveformContainerRef = useRef(null);
  const audioPlayback = useAudioPlayback();

  // Check if manager is already playing this recording
  const isManagerPlaying = audioPlayback.state.recordingId === recordingId;

  const [localState, setLocalState] = useState(() => ({
    currentPosition: isManagerPlaying ? audioPlayback.state.currentPosition : 0,
    duration: isManagerPlaying ? audioPlayback.state.duration : propDuration,
    enabled: isManagerPlaying,
    playing: isManagerPlaying ? audioPlayback.state.playing : false,
    ready: isManagerPlaying ? audioPlayback.state.ready : false,
    url,
  }));

  // Track previous props for comparison
  const prevPropsRef = useRef({ url, recordingId });
  // Track if we were playing to detect when another recording takes over
  const wasPlayingRef = useRef(localState.playing);

  // Handle manager state changes
  useEffect(() => {
    if (audioPlayback.state.recordingId === recordingId) {
      setLocalState(prev => ({
        ...prev,
        playing: audioPlayback.state.playing,
        currentPosition: audioPlayback.state.currentPosition,
        duration: audioPlayback.state.duration,
        ready: audioPlayback.state.ready,
      }));
      wasPlayingRef.current = audioPlayback.state.playing;
    } else if (audioPlayback.state.recordingId !== null && audioPlayback.state.recordingId !== recordingId) {
      // Manager switched to a different recording - reset our UI to disabled state
      // so the user clicks through play() (which routes to the correct recording)
      // rather than togglePlay() (which would toggle whatever's active in the manager).
      setLocalState(prev =>
        prev.enabled ? { ...prev, enabled: false, playing: false, currentPosition: 0, ready: false } : prev
      );
      wasPlayingRef.current = false;
    }
  }, [audioPlayback.state, recordingId]);

  // Handle prop changes (equivalent to UNSAFE_componentWillReceiveProps)
  useEffect(() => {
    const prevProps = prevPropsRef.current;
    if (url !== prevProps.url || recordingId !== prevProps.recordingId) {
      // Stop manager if it's playing the old recording
      if (audioPlayback.hasActiveRecording(prevProps.recordingId)) {
        audioPlayback.stop();
      }
      setLocalState({
        currentPosition: 0,
        duration: propDuration,
        enabled: false,
        playing: false,
        ready: false,
        url,
      });
    }
    prevPropsRef.current = { url, recordingId };
  }, [url, recordingId, propDuration, audioPlayback]);

  // Attach to container on mount if manager already has this recording active
  // This handles virtualization remount (user scrolls back to a playing recording)
  // Note: When user clicks play, startPlaybackWithManager passes container to play() directly,
  // so this effect is only needed for the remount case
  useEffect(() => {
    if (audioPlayback.hasActiveRecording(recordingId) && waveformContainerRef.current) {
      audioPlayback.attachToContainer(recordingId, waveformContainerRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle unmount - detach from container but keep playing
  useEffect(() => {
    return () => {
      if (audioPlayback.isPlayingRecording(recordingId)) {
        audioPlayback.detachFromContainer(recordingId);
      }
    };
  }, [recordingId, audioPlayback]);

  const trackPlayedRecording = useCallback(
    playDuration => {
      analytics.track('Voice Recording played', {
        duration: playDuration,
        recordingId,
        customerId,
        contentType,
      });
    },
    [recordingId, customerId, contentType]
  );

  const startPlaybackWithManager = useCallback(() => {
    audioPlayback.play({
      url: localState.url,
      recordingId,
      duration: propDuration,
      container: waveformContainerRef.current,
      onReady,
      analyticsCallback: trackPlayedRecording,
    });
  }, [audioPlayback, localState.url, recordingId, propDuration, onReady, trackPlayedRecording]);

  // Start playback when enabled changes from false to true
  const prevEnabledRef = useRef(localState.enabled);
  useEffect(() => {
    if (!prevEnabledRef.current && localState.enabled) {
      startPlaybackWithManager();
    }
    prevEnabledRef.current = localState.enabled;
  }, [localState.enabled, startPlaybackWithManager]);

  const handleClickEnable = useCallback(() => {
    if (disabled) {
      return;
    }
    setLocalState(prev => ({ ...prev, enabled: true }));
  }, [disabled]);

  const handleTogglePlay = useCallback(() => {
    if (disabled) {
      return;
    }
    audioPlayback.togglePlay();
  }, [disabled, audioPlayback]);

  const handlePlaybackRateChange = useCallback(
    rate => {
      audioPlayback.setPlaybackRate(rate);
    },
    [audioPlayback]
  );

  const playbackRateOptions = useMemo(() => PLAYBACK_RATES.map(rate => ({ label: `${rate}x`, value: rate })), []);

  const elapsedTime = Timer.displayDuration(localState.currentPosition * 1000);
  const totalTime = Timer.displayDuration(localState.duration * 1000);

  if (!localState.enabled) {
    const isPermissionDisabled = disabled;
    const containerClasses = classnames('wavePlayer-disabled', {
      'wavePlayer-disabled-permission': isPermissionDisabled,
    });
    return (
      <div
        className={containerClasses}
        onClick={isPermissionDisabled ? _.noop : handleClickEnable}
        title={isPermissionDisabled ? "You don't have permission to listen to this recording" : undefined}
      >
        <div className="wavePlayer-duration">{`${elapsedTime} / ${totalTime}`}</div>
        <div className="wavePlayer-disabled-background" />
        <i className={classnames('wavePlayer-playStopButton', 'icon-play', { disabled: isPermissionDisabled })} />
      </div>
    );
  }

  const isDisabled = !localState.ready || disabled;
  const classes = classnames('wavePlayer-playStopButton', {
    'icon-stop': localState.playing,
    'icon-play': !localState.playing,
    disabled: isDisabled,
  });
  const containerClasses = classnames('wavePlayer-container', {
    'wavePlayer-container-disabled': disabled,
  });

  return (
    <div className={containerClasses}>
      <div className="wavePlayer-waveformControlsContainer">
        <div className="wavePlayer-waveformContainer" ref={waveformContainerRef}>
          {isDisabled && !disabled ? (
            <span className="wavePlayer-spinner wavePlayer-spinner-visible">
              <i className="fa fa-spinner fa-pulse" />
            </span>
          ) : null}
        </div>
        <div className="wavePlayer-controlsContainer">
          <i
            className={classes}
            onClick={isDisabled ? _.noop : handleTogglePlay}
            title={disabled ? "You don't have permission to listen to this recording" : undefined}
          />
        </div>
      </div>
      <div className="wavePlayer-footerRow">
        <div className="wavePlayer-duration">{`${elapsedTime} / ${totalTime}`}</div>
        <DropdownMenu
          data-aid="wavePlayer-playbackRate"
          disabled={isDisabled}
          maxHeight={500}
          onSelect={handlePlaybackRateChange}
          openerComponent={PlaybackRateOpener}
          options={playbackRateOptions}
          text={`${audioPlayback.state.playbackRate}x`}
          value={audioPlayback.state.playbackRate}
        />
      </div>
    </div>
  );
}

WavePlayer.propTypes = {
  contentType: PropTypes.string.isRequired,
  customerId: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  onReady: PropTypes.func.isRequired,
  recordingId: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default WavePlayer;
